import React, { useState, useEffect } from 'react';

const SpeedReadingRacerGame = ({ gameData, onComplete }) => {
    const [gamePhase, setGamePhase] = useState('ready'); // ready, reading, quiz, complete
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [feedback, setFeedback] = useState(null);

    const currentQuestion = gameData?.questions?.[currentQuestionIndex];

    const startReading = () => {
        setStartTime(Date.now());
        setGamePhase('reading');
        if (navigator.vibrate) navigator.vibrate(50);
    };

    const finishReading = () => {
        setEndTime(Date.now());
        setGamePhase('quiz');
        if (navigator.vibrate) navigator.vibrate(30);
    };

    const calculateWPM = () => {
        if (!startTime || !endTime) return 0;
        const timeInMinutes = (endTime - startTime) / 60000;
        const wpm = Math.round(gameData.word_count / timeInMinutes);
        return wpm;
    };

    const handleOptionSelect = (option) => {
        if (selectedOption || feedback) return;
        if (navigator.vibrate) navigator.vibrate(20);
        setSelectedOption(option);

        if (option === currentQuestion.correct_answer) {
            setFeedback('correct');
            const pointsToAdd = gameData.points_per_question || 20;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                setSelectedOption(null);

                if (currentQuestionIndex < gameData.questions.length - 1) {
                    setCurrentQuestionIndex(prev => prev + 1);
                } else {
                    setGamePhase('complete');
                    if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
                    setTimeout(() => onComplete?.(newScore), 4000);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                setSelectedOption(null);
            }, 2000);
        }
    };

    if (!gameData || !gameData.passage || !gameData.questions) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-rose-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🏎️</div>
                <h3 className="text-base md:text-3xl font-black text-rose-900 tracking-tighter uppercase">Ruošiama trasa...</h3>
            </div>
        );
    }

    const wpm = calculateWPM();

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Dashboard / Progress Area */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-rose-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
                            🏎️
                        </div>
                        <div>
                            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                                Skaitymo Lenktynės
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Perskaityk tekstą greitai ir įrodyk savo žinias!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Rezultatas</p>
                            <p className="text-2xl font-black text-rose-600 leading-none">⭐ {score}</p>
                        </div>
                        {gamePhase === 'quiz' && (
                            <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner text-center min-w-[100px]">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Etapas</p>
                                <p className="text-2xl font-black text-slate-800 leading-none">{currentQuestionIndex + 1}/{gameData.questions.length}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Stage: Ready */}
            {gamePhase === 'ready' && (
                <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-12 lg:p-4 md:p-20 text-center shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(244,63,94,0.05)_0%,transparent_70%)] pointer-events-none" />
                    <div className="text-[160px] animate-bounce drop-shadow-2xl">🏁</div>
                    <div className="space-y-6">
                        <h4 className="text-2xl md:text-6xl font-black text-slate-800 tracking-tighter uppercase leading-none">Pasiruošk!</h4>
                        <p className="text-2xl text-slate-500 font-bold max-w-2xl italic font-serif leading-relaxed">
                            "Skaitymo lenktynės prasideda! Spausk startą, perskaityk visą tekstą kuo greičiau ir pasiruošk kontroliniams klausimams."
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 md:p-8 mt-12 w-full max-w-lg">
                        <div className="bg-white rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-4 border-rose-100 shadow-xl">
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Apimtis</p>
                            <p className="text-xl md:text-5xl font-black text-rose-600 tracking-tighter">{gameData.word_count} žod.</p>
                        </div>
                        <div className="bg-white rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-4 border-sky-100 shadow-xl">
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Užduotys</p>
                            <p className="text-xl md:text-5xl font-black text-sky-600 tracking-tighter">{gameData.questions.length} kl.</p>
                        </div>
                    </div>

                    <button
                        onClick={startReading}
                        className="premium-button-3d mt-16 px-24 py-4 md:py-10 bg-gradient-to-r from-rose-500 to-orange-600 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-2xl shadow-rose-500/20 uppercase tracking-widest group active:scale-95 transition-all"
                    >
                        STARTAS 🚦
                    </button>
                </div>
            )}

            {/* Stage: Reading */}
            {gamePhase === 'reading' && (
                <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                    <div className="absolute top-0 right-0 p-3 md:p-8 text-8xl opacity-[0.03] select-none pointer-events-none">📖</div>
                    <div className="bg-white/90 rounded-xl md:rounded-[3rem] p-4 md:p-10 md:p-4 md:p-20 shadow-2xl border-4 border-rose-50 mb-12 relative max-w-5xl w-full">
                        <div className="prose prose-2xl max-w-none text-slate-800 font-bold leading-relaxed font-sans first-letter:text-8xl first-letter:font-black first-letter:text-rose-600 first-letter:mr-4 first-letter:float-left drop-shadow-sm select-text">
                            {gameData.passage}
                        </div>
                    </div>

                    <button
                        onClick={finishReading}
                        className="premium-button-3d w-full max-w-2xl py-4 md:py-10 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-2xl shadow-emerald-500/20 uppercase tracking-widest group active:scale-95 transition-all"
                    >
                        ✅ BAIGIAU SKAITYTI
                    </button>
                </div>
            )}

            {/* Stage: Quiz */}
            {gamePhase === 'quiz' && currentQuestion && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:p-8 items-start">
                    {/* Speedometer Card */}
                    <div className="lg:col-span-4">
                        <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 bg-slate-900 text-white shadow-2xl border-4 border-slate-800 relative overflow-hidden text-center h-full flex flex-col justify-center">
                            <div className="absolute top-0 left-0 w-40 h-40 bg-rose-500/10 rounded-full blur-3xl" />
                            <div className="absolute bottom-0 right-0 w-40 h-40 bg-sky-500/10 rounded-full blur-3xl" />
                            
                            <h5 className="text-[10px] font-black text-rose-400 uppercase tracking-[0.4em] mb-4">Dabartinis Tempas</h5>
                            <div className="relative inline-block">
                                <div className="text-xl md:text-5xl md:text-[120px] font-black leading-none tracking-tighter drop-shadow-[0_0_30px_rgba(244,63,94,0.4)] italic">
                                    {wpm}
                                </div>
                                <div className="text-2xl font-black text-rose-500 uppercase tracking-widest italic mt-2">WPM</div>
                            </div>
                            <p className="text-slate-500 font-bold mt-6 italic font-serif">"Tavo greitis stulbinantis, bet ar viską supratai?"</p>
                        </div>
                    </div>

                    {/* Question Card */}
                    <div className="lg:col-span-8">
                        <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl">
                            <div className="space-y-4 md:space-y-10">
                                <div className="text-center space-y-4">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.5em]">Patikra</h4>
                                    <h3 className="text-lg md:text-4xl md:text-xl md:text-5xl font-black text-slate-800 tracking-tighter leading-tight italic font-serif">
                                        "{currentQuestion.question}"
                                    </h3>
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                    {currentQuestion.options.map((option, index) => {
                                        const isSelected = selectedOption === option;
                                        const isCorrectTarget = feedback === 'correct' && isSelected;
                                        const isWrongTarget = feedback === 'wrong' && isSelected;
                                        const isRevealCorrect = feedback === 'wrong' && option === currentQuestion.correct_answer;

                                        return (
                                            <button
                                                key={index}
                                                onClick={() => handleOptionSelect(option)}
                                                disabled={!!feedback}
                                                className={`
                                                    flex items-center gap-3 md:p-8 p-3 md:p-8 rounded-lg md:rounded-[2.5rem] text-2xl font-black transition-all duration-300 text-left border-b-8
                                                    ${isCorrectTarget
                                                        ? 'bg-emerald-500 text-white border-emerald-700 scale-105 shadow-[0_0_40px_rgba(16,185,129,0.5)]'
                                                        : isWrongTarget
                                                            ? 'bg-rose-500 text-white border-rose-700 animate-shake'
                                                            : isRevealCorrect
                                                                ? 'bg-emerald-500/50 text-white border-emerald-700'
                                                                : 'bg-white text-slate-800 border-slate-200 hover:bg-rose-50 active:translate-y-2 active:border-b-0 shadow-lg'}
                                                    disabled:cursor-default
                                                `}
                                            >
                                                <div className={`
                                                    w-14 h-14 rounded-2xl flex items-center justify-center text-xl shrink-0 border-4
                                                    ${isSelected || isRevealCorrect ? 'bg-white/20 border-white/40 text-white' : 'bg-slate-100 border-slate-200 text-slate-400'}
                                                `}>
                                                    {String.fromCharCode(65 + index)}
                                                </div>
                                                <span className="flex-1 tracking-tight">{option}</span>
                                                {isSelected && (
                                                    <div className="text-lg md:text-4xl animate-pop-in">
                                                        {isCorrectTarget ? '✅' : '❌'}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Stage: Complete */}
            {gamePhase === 'complete' && (
                <div className="premium-glass rounded-3xl md:rounded-[5rem] p-4 md:p-16 lg:p-24 text-center border-white/60 shadow-2xl animate-pop-in bg-white relative overflow-hidden flex flex-col items-center">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(244,63,94,0.05)_0%,transparent_70%)] pointer-events-none" />
                    <div className="text-2xl md:text-6xl md:text-[180px] mb-8 drop-shadow-2xl animate-float">🥇🏆</div>
                    <div className="space-y-6 mb-12">
                        <h3 className="text-7xl font-black text-slate-800 tracking-tighter uppercase leading-none">Lenktynės Baigtos!</h3>
                        <p className="text-base md:text-3xl font-bold text-slate-400 italic font-serif">"Esi tikras greito skaitymo ir suvokimo čempionas!"</p>
                    </div>
                    
                    <div className="bg-slate-900 rounded-2xl md:rounded-[4rem] p-4 md:p-14 text-white shadow-[0_40px_100px_rgba(0,0,0,0.4)] border-8 border-slate-800 mb-14 w-full max-w-2xl relative">
                        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(244,63,94,0.2)_0%,transparent_100%)] pointer-events-none" />
                        <p className="text-xs font-black text-rose-500 uppercase tracking-[0.5em] mb-6">Tavo Finalinis Rezultatas</p>
                        <div className="flex items-center justify-center gap-4 md:p-10">
                            <div className="text-center">
                                <p className="text-xl md:text-5xl md:text-[120px] font-black italic leading-none text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]">{wpm}</p>
                                <p className="text-xl font-black uppercase tracking-widest mt-4 text-rose-500">WPM</p>
                            </div>
                            <div className="w-1 h-32 bg-slate-700 rounded-full" />
                            <div className="text-center">
                                <p className="text-xl md:text-5xl md:text-[120px] font-black italic leading-none text-emerald-400 drop-shadow-[0_0_30px_rgba(52,211,153,0.3)]">{score}</p>
                                <p className="text-xl font-black uppercase tracking-widest mt-4 text-emerald-500">TAŠKAI</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-center gap-4">
                        <div className="flex gap-2">
                            <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce" />
                            <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                            <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                        </div>
                        <span className="text-sm font-black text-slate-300 uppercase tracking-[0.3em] ml-4">Registruojamas čempionas...</span>
                    </div>
                </div>
            )}

            {/* Global Context Feedback Overlay */}
            {feedback && (
                <div className="fixed inset-0 pointer-events-none flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className={`
                        px-5 md:px-16 py-12 rounded-2xl md:rounded-[4rem] shadow-[0_40px_100px_rgba(0,0,0,0.3)] backdrop-blur-2xl border-4 animate-pop-in
                        ${feedback === 'correct' ? 'bg-emerald-500/90 border-emerald-400 text-white' : 'bg-rose-500/90 border-rose-400 text-white'}
                    `}>
                        <div className="text-xl md:text-5xl md:text-[120px] text-center mb-6 drop-shadow-lg animate-bounce">
                            {feedback === 'correct' ? '🏁' : '🏎️'}
                        </div>
                        <h3 className="text-xl md:text-5xl font-black text-center tracking-tighter uppercase leading-none">
                            {feedback === 'correct' ? 'TIKRAI TAIP!' : 'BANDYK DAR KARTĄ!'}
                        </h3>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpeedReadingRacerGame;
